import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../components/ConfirmModal";
import { useRefreshGuard } from "./refreshGuard";

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

// True when any field differs between the two objects. Empty values
// (undefined / null / "" / []) are treated as equal to each other.
export const hasFormChanges = (initial = {}, current = {}) => {
  const keys = new Set([...Object.keys(initial || {}), ...Object.keys(current || {})]);

  return [...keys].some((key) => {
    const initialValue = initial?.[key];
    const currentValue = current?.[key];
    if (isEmptyValue(initialValue) && isEmptyValue(currentValue)) return false;

    return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
  });
};

// Asks for confirmation before an in-app Cancel / Back / Close discards unsaved changes.
// `isDirty` may be a boolean or a function (checked lazily, for values kept outside React state).
// With `warnOnUnload`, closing or reloading the tab also asks while dirty.
// Browser back / route changes can't be blocked with BrowserRouter - only the controls
// that go through `confirmLeave` are covered.
export const useUnsavedChanges = (isDirty, { warnOnUnload = false } = {}) => {
  const { t } = useTranslation("common");
  const [pendingAction, setPendingAction] = useState(null);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const checkDirty = useCallback(() => {
    const dirty = isDirtyRef.current;
    return typeof dirty === "function" ? !!dirty() : !!dirty;
  }, []);

  // A pull-to-refresh would remount the page and lose the changes too
  useRefreshGuard(checkDirty);

  useEffect(() => {
    if (!warnOnUnload) return undefined;

    const handleBeforeUnload = (event) => {
      if (!checkDirty()) return;
      event.preventDefault();
      // Required by some browsers to show the native prompt
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [warnOnUnload, checkDirty]);

  // Runs `action` right away when there is nothing to lose, otherwise asks first
  const confirmLeave = useCallback(
    (action) => {
      if (checkDirty()) {
        setPendingAction(() => action);
      } else {
        action();
      }
    },
    [checkDirty]
  );

  const handleDiscard = () => {
    const action = pendingAction;
    setPendingAction(null);
    action?.();
  };

  const unsavedChangesModal = pendingAction ? (
    <ConfirmModal
      title={t("unsaved_changes_title")}
      text={t("unsaved_changes_text")}
      handleAction={handleDiscard}
      onClose={() => setPendingAction(null)}
    />
  ) : null;

  return { confirmLeave, unsavedChangesModal };
};

export default useUnsavedChanges;
